import "./KcApp.scss";
import {lazy, Suspense, useEffect, useState} from "react";
import Fallback, {type PageProps, useDownloadTerms} from "keycloakify/login";
import type {KcContext} from "./kcContext";
import {useI18n} from "./i18n";
import LoginResetPassword from "./pages/LoginResetPassword";
import LoginUpdatePassword from "./pages/LoginUpdatePassword";
import LoginOtp from "./pages/LoginOtp";
import LoginConfigTotp from "./pages/LoginConfigTotp";
import {countryButtons, getTos} from "./countryUrls";
import {retrieveQueryParamFromUrl} from "oidc-spa/tools/urlQueryParams";
import InfinigateContext, {InfinigateContextType} from "./infinigateContext";
import LogoutConfirm from "./pages/LogoutConfirm";
import {getCountryCode} from "./salesChannels";

const Template = lazy(() => import("./Template"));
const Login = lazy(() => import("./pages/Login"));
const Info = lazy(() => import("./pages/Info"));
const Terms = lazy(() => import("./pages/Terms"));
// If you can, favor register-user-profile.ftl over register.ftl, see: https://docs.keycloakify.dev/realtime-input-validation
const Register = lazy(() => import("./pages/Register"));
const RegisterUserProfile = lazy(() => import("./pages/RegisterUserProfile"));

// This is like adding classes to theme.properties
// https://github.com/keycloak/keycloak/blob/11.0.3/themes/src/main/resources/theme/keycloak/login/theme.properties
const classes: PageProps<any, any>["classes"] = {
    // NOTE: The classes are defined in ./KcApp.css
    "kcHtmlClass": "my-root-class",
    "kcHeaderWrapperClass": "my-color my-font",
    "kcLoginClass": ""
};

export default function KcApp(props: { kcContext: KcContext; }) {

    const {kcContext} = props;

    const getShopwareCountry = () => {
        let salesChannelParam = retrieveQueryParamFromUrl({
            "url": window.location.href,
            "name": "sw_saleschannelid"
        });
        let countryCodeParam = retrieveQueryParamFromUrl({
            "url": window.location.href,
            "name": "sw_country"
        });
        if (salesChannelParam.wasPresent) {
            const countryCode = getCountryCode(salesChannelParam.value);
            localStorage.setItem('shopwareCountry', countryCode);
            return countryCode;
        } else if (countryCodeParam.wasPresent) {
            localStorage.setItem('shopwareCountry', countryCodeParam.value);
            return countryCodeParam.value;
        }
        return null;
    }

    const getKcLocale = () => {
        const result = retrieveQueryParamFromUrl({
            "url": window.location.href,
            "name": "kc_locale",
        });
        if (result.wasPresent) {
            return result.value;
        }
        return null;
    };

    const getRedirectTo = () => {
        const result = retrieveQueryParamFromUrl({
            "url": window.location.href,
            "name": "redirect_uri",
        });
        if (result.wasPresent) {
            return result.value;
        }
        return null;
    };

    const mapLocales = (code: string | null) => {
        if (!code) return null;
        if (code.length > 2) code = code.substring(0, 2);
        if (code === 'nb') code = 'no';
        if (code === 'se') code = 'sv';
        if (code === 'dk') code = 'da';
        return code;
    };

    const i18n = useI18n({kcContext});
    const [infinigateContext, setInfinigateContext] = useState<InfinigateContextType>({
        redirectTo: null,
        shopwareCountryCode: null,
        kcLocale: null,
        btnLinks: null
    });

    useDownloadTerms({
        kcContext,
        "downloadTermMarkdown": async ({currentLanguageTag}) => {
            let shopwareCountryCode = getShopwareCountry() || localStorage.getItem('shopwareCountry');
            return await fetch(
                (() => {
                    return getTos(shopwareCountryCode);
                })(),
            ).then(response => response.text());
        },
    });

    useEffect(() => {
        // unmapped locale which should be delivered (could be CH e.g. which is not valid)
        let kcLocale = mapLocales(getKcLocale());
        if (kcLocale && i18n?.labelBySupportedLanguageTag?.hasOwnProperty(kcLocale) && i18n?.currentLanguageTag !== kcLocale) {
            i18n?.changeLocale(kcLocale);
            return;
        }
        let btnLinks = null;
        let shopwareCountryCode = getShopwareCountry() || localStorage.getItem('shopwareCountry');
        if (shopwareCountryCode) {
            btnLinks = countryButtons(shopwareCountryCode);
        }
        const redirectTo = getRedirectTo();
        setInfinigateContext({redirectTo, shopwareCountryCode, kcLocale, btnLinks});

        if (kcContext.pageId === 'info.ftl' && shopwareCountryCode) {
            kcContext.pageRedirectUri = countryButtons(shopwareCountryCode)?.root;
            kcContext.skipLink = false;
        }
    }, [i18n]);

    if (i18n === null) {
        //NOTE: Text resources for the current language are still being downloaded, we can't display anything yet.
        //We could display a loading progress but it's usually a matter of milliseconds.
        return null;
    }
    /*
    * Examples assuming i18n.currentLanguageTag === "en":
    * i18n.msg("access-denied") === <span>Access denied</span>
    * i18n.msg("foo") === <span>foo in English</span>
    */
    return (
        <Suspense>
            <InfinigateContext.Provider value={infinigateContext}>
                {(() => {
                    switch (kcContext.pageId) {
                        case "login.ftl":
                            return <Login
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "logout-confirm.ftl":
                            return <LogoutConfirm
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "login-otp.ftl":
                            return <LoginOtp
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "login-config-totp.ftl":
                            return <LoginConfigTotp
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "login-update-password.ftl":
                            return <LoginUpdatePassword
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "login-reset-password.ftl":
                            return <LoginResetPassword
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />;
                        case "register.ftl":
                            return <Register
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={true}
                            />;
                        case "info.ftl":
                            return <Info
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={true}
                            />;
                        case "terms.ftl":
                            return <Terms
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={true}
                            />;
                        case "register-user-profile.ftl":
                            return <RegisterUserProfile
                                {...{kcContext, i18n, Template, classes}}
                                doUseDefaultCss={false}
                            />
                        default:
                            return <Fallback
                                {...{kcContext, i18n, classes}}
                                Template={Template}
                                doUseDefaultCss={false}
                            />;
                    }
                })()}
            </InfinigateContext.Provider>
        </Suspense>
    );

}
